"use client";

import React, {memo} from "react";
import {ChannelModelCombobox} from "@/components/assistants/ChannelModelCombobox";
import {useTranslation} from "react-i18next";
import AssistantSelector, {type Assistant} from "../AssistantSelector";

interface AssistantModelControlsProps {
   showAssistantSelector: boolean;
   assistants: Assistant[];
   selectedAssistant?: Assistant;
   onAssistantChange?: (assistant: Assistant) => void;
   assistantSelectorDisabled: boolean;
   isLoadingAssistants: boolean;
   showModelSelector: boolean;
   onModelChange?: (assistantId: string, newModel: string) => void;
   canEditModel?: boolean;
   isModelSaving?: boolean;
   conversationId?: string | null;
}

const AssistantModelControls: React.FC<AssistantModelControlsProps> = ({
   showAssistantSelector,
   assistants,
   selectedAssistant,
   onAssistantChange,
   assistantSelectorDisabled,
   isLoadingAssistants,
   showModelSelector,
   onModelChange,
   canEditModel,
   isModelSaving,
   conversationId,
}) => {
   const {t: ct} = useTranslation("common");

   const handleModelChange =
      onModelChange && selectedAssistant
         ? (newModel: string) => onModelChange(selectedAssistant.id, newModel)
         : undefined;

   return (
      <>
         {showAssistantSelector && assistants && selectedAssistant && onAssistantChange ? (
            <div data-tour="assistant-selector">
               <AssistantSelector
                  assistants={assistants}
                  selectedAssistant={selectedAssistant}
                  onAssistantChange={onAssistantChange}
                  className="min-w-0"
                  disabled={assistantSelectorDisabled}
                  onModelChange={handleModelChange}
                  canEditModel={canEditModel}
                  isModelSaving={isModelSaving}
                  conversationId={conversationId}
               />
            </div>
         ) : !showAssistantSelector && showModelSelector && selectedAssistant?.model ? (
            <ChannelModelCombobox
               currentModel={selectedAssistant.model}
               onChange={handleModelChange}
               readOnly={!canEditModel}
               readOnlyClassName="inline-flex items-center gap-1.5 text-xs text-400 dark:text-500 font-normal cursor-default [&_svg]:text-[10px] [&_svg]:opacity-60"
               triggerClassName="!border-0 !shadow-none !bg-transparent dark:!bg-transparent hover:!bg-transparent hover:!text-500 !rounded-none !px-0 !py-0 !h-auto !min-h-0 !text-xs !font-normal !text-400 dark:!text-500 !gap-1.5 !w-auto !max-w-48 md:!max-w-64 transition-colors [&_svg]:!text-[10px] [&_svg]:!opacity-60"
               contentClassName="w-64 rounded-xl bg-background border border-neutral shadow-lg focus:ring-0 [&_[cmdk-input-wrapper]]:hidden p-1"
               emptyMessage={ct("noResults")}
               disabled={isModelSaving}
               conversationId={conversationId}
            />
         ) : (
            isLoadingAssistants && <div className="w-32 h-8 rounded-lg bg-200 animate-pulse" />
         )}
      </>
   );
};

// The composer re-renders on every keystroke; these controls do not depend on the draft.
export default memo(AssistantModelControls);
